import React, { useCallback, useEffect, useMemo, useState,useRef } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData, SearchBox } from 'components/commonparts'
import { SearchCondition, DataList } from 'components/parts/MasterCalender';
import { useCalendar } from 'hooks/useCalendar';
import { useCenter } from 'hooks/useCenter';
import { useGridSearch } from 'hooks/useGridSearch';
import { useSearchParamStore } from 'store/searchParamStore';
import { getLocalSearchParam } from 'utils/getLocalSearchParam';

import type { GetMstCalendarCalendarGetRequest, MstCalendar, ResponseResult } from 'api-client';

function MasterCalendar() {
  const { loginUserInfo, typeCode: typeCodes } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
  const localOrgCenterId = localSearchParam?.calendar?.orgCenterId || '' 
  const localSelectDateRange = localSearchParam?.calendar?.selectDateRange || undefined

  const defaultSelectDateRange = useMemo(() => {
    if (!typeCodes || typeCodes.length === 0) return undefined
    const group = typeCodes.find((type) => type.typeGroup === 'select_date_range')
    const defaultItem = group?.codes?.find((code) => code.defaultFlg)
    return defaultItem?.typeCode ?? group?.codes?.[0]?.typeCode
  }, [typeCodes])

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstCalendarCalendarGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
    orgCenterId: localOrgCenterId,
    selectDateRange: localSelectDateRange ?? defaultSelectDateRange,
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstCalendar[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const { centerSearch, fetchCenterSearch } = useCenter()

  const { calendarSearch, fetchCalendarSearch, calendarSearchResult, isLoading } = useCalendar()
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })
  const [searchParam, setSearchParam] = useState<GetMstCalendarCalendarGetRequest>(initSearchParam)
  const isFirstRender = useRef(true);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    setResultMessage(calendarSearchResult)
  }, [calendarSearchResult])

  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])

  useEffect(() => {
    setSearchParam((prev) => ({
      ...prev,
      selectDateRange: prev.selectDateRange ?? defaultSelectDateRange,
    }))
  }, [defaultSelectDateRange])

  const doSearch = useCallback(async (requestParam: GetMstCalendarCalendarGetRequest) => {
    try {
      fetchCalendarSearch({
        enabled: false,
          ...requestParam,
      });
  
      if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
        setSearchParamState<GetMstCalendarCalendarGetRequest>(
          loginUserInfo?.tenantId,
          loginUserInfo?.companyId,
          'Calendar',
          {
            ...requestParam,
          }
        );
      }
  
        setSearchParam(requestParam);
        setHasGridEdited(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      } 
    }, [
      hasGridEdited,
      setDiscardChangeDialogOpen,
      fetchCalendarSearch,
      loginUserInfo,
      setSearchParamState,
      setSearchParam,
      setHasGridEdited
  ]);

  const handleRegister = () => {
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };
  const onReload = () => {
    // 検索処理の実行を行うトリガー
    // 子コンポーネントからの検索命令⇒検索条件の検索を実行⇒doSearchがコールされる
    setSearchTrigger((prev) => !prev);
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current = false
        
        const calendarPromise = fetchCalendarSearch({
          enabled: false,
          ...searchParam,
        });
        const centerPromise = fetchCenterSearch({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        await Promise.all([calendarPromise, centerPromise]);
      }
    }
    firstTimeRender();
  }, [isFirstRender, searchParam]);

  const generateDownloadData = (): MstCalendar[] => {
    if (!calendarSearch?.list || calendarSearch.list.length === 0) {
      return [{} as MstCalendar]
    }
    const downloadData: MstCalendar[] = [...calendarSearch.list]
    return downloadData
  }

  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }


  return (
    <Box>
      <MasterSearchFrom
          onReload={onReload}
          result={resultMessage}
          onDoUpdate={handleRegister}
          onImportData={handleUploadData}
          onFileDownLoad={handleFileDownload}
          discardChangeDialogOpen={discardChangeDialogOpen}
          setDownloadMode={setDownloadMode}
          importRequiredFields={["org_center_id","calendar_date"]}
          headerJp={["自社センターID","日付"]}
          lastUpdateDatetime={calendarSearch?.lastUpdateDatetime}
          hasDataEdited={hasGridEdited}
      >
        <SearchCondition
            hasDataEdited={hasGridEdited}
            searchParam={searchParam}
            onSearch={doSearch}
            setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
            searchTrigger={searchTrigger}
        />
      </MasterSearchFrom>
      <FileDownloadData fileName="カレンダーマスタ" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      <DataList
          list={calendarSearch}
          onReload={onReload}
          center={centerSearch.list}
          isLoading={isLoading}
          searchParam={searchParam}
          onUploadData={uploadData}
          updateTrigger={updateTrigger}
          setHasGridEdited={setHasGridEdited}
          setResultMessage={setGridMessage}
          setDownloadColumnDefs={setDownloadColDef}
          gridRef={gridSearch.gridRef}
          findSearchValue={gridSearch.gridSearchProps.findSearchValue}
          onFindChanged={gridSearch.gridSearchProps.onFindChanged}
          isSearchVisible={gridSearch.searchBoxProps.isVisible}
        />
      
    </Box>
          
  )
}

export default MasterCalendar;
